import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  acceptBonusCampaign,
  getActiveBonusCampaigns,
  getBonusCampaign,
  handleError,
} from '@homeplay/api';
import {
  SiteContext,
  bonusCampaignsUpdated,
  selectActiveCampaignBonusBalance,
  selectBonusCampaignsVersion,
  selectUnitToken,
  selectUser,
  setUnitError,
} from '@homeplay/app';
import {
  formatBalance,
  formatBonusCampaignDate,
  getBonusCampaignDaysLeft,
} from '@homeplay/utils';
import { Page } from '@homeplay/components';
import Button from '@homeplay/components/src/UI/Button';
import Checkbox from '@homeplay/components/src/UI/Checkbox';
import ConfirmModal from '@homeplay/components/src/Modal/ConfirmModal';
import {
  BonusCampaignLabels,
  BonusCampaignProgress,
  useTimeLeft,
} from './BonusCampaignParts';
import {
  getBonusCampaignBanner,
  getBonusCampaignStepsText,
  getPromoPath,
  getSitePath,
} from './helpers';
import classes from './BonusCampaigns.module.scss';

const BonusCampaignDetails = () => {
  const { bonusId } = useParams();
  const { siteName } = useContext(SiteContext);
  const dispatch = useDispatch();
  const unitToken = useSelector(selectUnitToken);
  const user = useSelector(selectUser);
  const version = useSelector(selectBonusCampaignsVersion);
  const activeBonusBalance = useSelector(selectActiveCampaignBonusBalance);
  const [bonusCampaign, setBonusCampaign] = useState(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const bonusState = bonusCampaign?.bonus_state || {};
  const status = user ? bonusState.status : null;
  const timeLeft = useTimeLeft(
    status === 'pending' ? bonusCampaign?.end_timestamp : null
  );

  useEffect(() => {
    if (!unitToken) return;

    let isActive = true;
    getBonusCampaign(unitToken, bonusId)
      .then((data) => isActive && setBonusCampaign(data))
      .catch((error) => {
        if (isActive) {
          dispatch(
            setUnitError({
              error: handleError(error, getPromoPath(siteName)),
            })
          );
        }
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitToken, bonusId, user, version]);

  const acceptBonus = async ({ shouldCheckActiveBonuses }) => {
    setIsAccepting(true);
    try {
      if (shouldCheckActiveBonuses) {
        const activeBonuses = await getActiveBonusCampaigns(unitToken);
        if (activeBonuses.length) {
          setIsConfirmOpen(true);
          return;
        }
      }

      await acceptBonusCampaign(unitToken, bonusCampaign.id);
      setIsConfirmOpen(false);
      dispatch(bonusCampaignsUpdated());
    } catch (error) {
      setIsConfirmOpen(false);
      dispatch(setUnitError({ error: handleError(error) }));
    } finally {
      setIsAccepting(false);
    }
  };

  const renderStatus = () => {
    switch (status) {
      case 'active':
        return (
          <div className={classes.StatusBox}>
            <div className={classes.StatusRow}>
              <span className="color-primary">Bonus is active</span>
              <strong>
                {getBonusCampaignDaysLeft(bonusState.expiration_timestamp)} days
                left
              </strong>
            </div>
            <p className={classes.StatusHint}>Wagering progress</p>
            <BonusCampaignProgress
              percentage={bonusState.completion_percentage}
              isLarge
            />
            <div className={classes.StatusRow}>
              <span>Bonus balance</span>
              <strong>{formatBalance(activeBonusBalance)}</strong>
            </div>
          </div>
        );
      case 'pending':
        return (
          <div className={classes.StatusBox}>
            <div className={classes.StatusRow}>
              <span className="color-primary">Waiting for deposit</span>
              <strong>{timeLeft}</strong>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderActions = () => {
    if (!user) {
      return (
        <Button navPath={getSitePath(siteName, 'auth')}>Sign In / Sign Up</Button>
      );
    }

    switch (status) {
      case 'available':
        return (
          <>
            <Checkbox
              id="bonus-campaign-terms"
              text="I have read and agreed to the T&C's of the Bonus"
              checked={isTermsAccepted}
              onChange={() => setIsTermsAccepted((prev) => !prev)}
            />
            <Button
              onClick={() => acceptBonus({ shouldCheckActiveBonuses: true })}
              disabled={!isTermsAccepted || isAccepting}
            >
              Accept
            </Button>
          </>
        );
      case 'pending':
        return (
          <Button navPath={getSitePath(siteName, 'cashin')}>Deposit</Button>
        );
      case 'active':
        return <Button navPath={getSitePath(siteName)}>Play</Button>;
      default:
        return null;
    }
  };

  return (
    <Page
      headerContent={'Promotions'}
      backPath={getPromoPath(siteName)}
      customClass={classes.BonusCampaigns}
    >
      {bonusCampaign ? (
        <div className={'container ' + classes.Details}>
          <div className={classes.DetailsBanner}>
            <img
              src={getBonusCampaignBanner(bonusCampaign, true)}
              alt={bonusCampaign.name}
            />
            <BonusCampaignLabels type={bonusCampaign.type} />
          </div>

          <h1 className={classes.DetailsTitle}>{bonusCampaign.name}</h1>

          {renderStatus()}

          <div
            className={classes.RichText}
            dangerouslySetInnerHTML={{ __html: bonusCampaign.description }}
          />

          {getBonusCampaignStepsText(bonusCampaign, !!user) && (
            <section>
              <h2 className={classes.SectionTitle + ' color-primary'}>
                Get your bonus in a few steps
              </h2>
              <div
                className={classes.RichText + ' ' + classes.Steps}
                dangerouslySetInnerHTML={{
                  __html: getBonusCampaignStepsText(bonusCampaign, !!user),
                }}
              />
            </section>
          )}

          <div className={classes.Expiry}>
            <span>Expiry</span>
            <strong>
              {formatBonusCampaignDate(
                bonusState.expiration_timestamp ?? bonusCampaign.end_timestamp
              )}
            </strong>
          </div>

          {bonusCampaign.terms_text && (
            <section>
              <h2 className={classes.SectionTitle + ' color-primary'}>
                Rules / Terms &amp; Conditions
              </h2>
              <div
                className={classes.RichText + ' ' + classes.Terms}
                dangerouslySetInnerHTML={{ __html: bonusCampaign.terms_text }}
              />
            </section>
          )}

          <div className={classes.Actions}>{renderActions()}</div>
        </div>
      ) : (
        <div className="container">
          <div className={classes.DetailsPlaceholder} />
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => acceptBonus({ shouldCheckActiveBonuses: false })}
        title="Warning"
        confirmLabel="Decline active and continue"
        cancelLabel="Don't accept"
        isLoading={isAccepting}
      >
        <p>Are you sure that you want to accept a new bonus?</p>
        <p>
          You already have an active bonus. If you accept another one,{' '}
          <span className="color-primary">
            the previous one will be cancelled
          </span>
          .
        </p>
      </ConfirmModal>
    </Page>
  );
};

export default BonusCampaignDetails;
